// Adapted from https://github.com/facebook/lexical/blob/v0.16.1/packages/lexical-playground/src/nodes/PillNode.ts

/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { qualifier } from "$root/utils";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	$applyNodeReplacement,
	type DOMConversionMap,
	type DOMConversionOutput,
	type DOMExportOutput,
	type EditorConfig,
	type LexicalNode,
	type NodeKey,
	type SerializedTextNode,
	type Spread,
	TextNode,
} from "lexical";
import type { Pill } from "./common";

export type SerializedPillNode = Spread<
	{
		pill: Pill;
	},
	SerializedTextNode
>;

function $convertPillElement(domNode: HTMLElement): DOMConversionOutput | null {
	const label = domNode.textContent;
	const value = domNode.getAttribute("data-pill-value");

	if (label && value) {
		const node = $createPillNode({ label, value });
		return {
			node,
		};
	}

	return null;
}

const pillStyle = `background-color: ${themeCSSVars.palette_A100}; cursor: pointer`;

export class PillNode<T extends string> extends TextNode {
	__pill: Pill<T>;
	__onClick?: () => void;

	static getType(): string {
		return "pill";
	}

	static clone<TValue extends string>(node: PillNode<TValue>): PillNode<TValue> {
		return new PillNode<TValue>(node.__pill, node.__onClick, node.__key);
	}
	static importJSON<TValue extends string>(serializedNode: SerializedPillNode): PillNode<TValue> {
		const node = $createPillNode<TValue>(serializedNode.pill);
		node.setTextContent(serializedNode.text);
		node.setFormat(serializedNode.format);
		node.setDetail(serializedNode.detail);
		node.setMode(serializedNode.mode);
		node.setStyle(serializedNode.style);
		return node;
	}

	constructor(pill: Pill<T>, onClick?: () => void, key?: NodeKey) {
		super(pill.label, key);
		this.__pill = pill;
		this.__onClick = onClick;
	}

	setOnClick(onClick: () => void): void {
		const self = this.getWritable();
		self.__onClick = onClick;
	}

	getLatestState(): this {
		const self = this.getLatest();
		return self;
	}

	exportJSON(): SerializedPillNode {
		return {
			...super.exportJSON(),
			pill: this.__pill,
			type: "pill",
			version: 1,
		};
	}

	applyDomAttributes(dom: HTMLElement): void {
		dom.setAttribute("data-lexical-pill", "true");
		dom.setAttribute("data-pill-value", this.__pill.value);
		dom.setAttribute("data-qualifier", qualifier.lexical.pillNode.node(this.__pill.value));
	}

	createDOM(config: EditorConfig): HTMLElement {
		const dom = document.createElement("span");
		dom.className = "pill";
		dom.textContent = this.__text;
		dom.style.cssText = pillStyle;

		if (this.__onClick) {
			dom.onclick = this.__onClick;
		}

		this.applyDomAttributes(dom);
		return dom;
	}

	exportDOM(): DOMExportOutput {
		const element = document.createElement("span");
		this.applyDomAttributes(element);
		element.textContent = this.__pill.label;
		return { element };
	}

	static importDOM(): DOMConversionMap | null {
		return {
			span: (domNode: HTMLElement) => {
				if (!domNode.hasAttribute("data-lexical-pill")) {
					return null;
				}
				return {
					conversion: $convertPillElement,
					priority: 1,
				};
			},
		};
	}

	isTextEntity(): true {
		return true;
	}

	canInsertTextBefore(): boolean {
		return false;
	}

	canInsertTextAfter(): boolean {
		return false;
	}
}

export function $createPillNode<T extends string>(pill: Pill, onClick?: () => void): PillNode<T> {
	const pillNode = new PillNode(pill, onClick);
	pillNode.setMode("segmented").toggleDirectionless();
	return $applyNodeReplacement(pillNode);
}

export function $isPillNode<T extends string>(node: LexicalNode | null | undefined): node is PillNode<T> {
	return node instanceof PillNode;
}
